import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PrimeArtifact } from '../../../types/primeArtifact';
import { ArtifactRelationship } from '../../../types/artifactRelationship';
import { Artifact } from '../../../types/artifact';
import { usePrimeArtifact, useFlatArtifacts } from '../../../store/hooks/usePrimeArtifact';
import { RadialView } from './RadialView';
import { LayeredView } from './LayeredView';
import { HierarchicalView } from './HierarchicalView';
import { NetworkView } from '../../../components/PrimeModel/RelationshipVisualization/NetworkView';
import { RelationshipService } from '../../../services/api/relationshipService';
import './RelationshipVisualizationPage.css';

type VisualizationType = 'radial' | 'layered' | 'hierarchical' | 'network';

export const RelationshipVisualizationPage: React.FC = () => {
  const { artifactId } = useParams<{ artifactId: string }>();
  const navigate = useNavigate();
  const [selectedArtifact, setSelectedArtifact] = useState<PrimeArtifact | null>(null);
  const [relationships, setRelationships] = useState<ArtifactRelationship[]>([]);
  const [visualizationType, setVisualizationType] = useState<VisualizationType>('radial');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const flatArtifacts = useFlatArtifacts();
  const { fetchArtifacts } = usePrimeArtifact();

  useEffect(() => {
    if (!artifactId || artifactId === 'select-artifact') {
      // Redirect to the artifacts page to select an artifact
      navigate('/prime/artifacts');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching data for artifactId:', artifactId);

        // Fetch all artifacts if not already loaded
        if (flatArtifacts.length === 0) {
          console.log('No artifacts loaded, fetching artifacts...');
          await fetchArtifacts();
          console.log('Artifacts fetched:', flatArtifacts);
        } else {
          console.log('Artifacts already loaded:', flatArtifacts.length);
        }

        // Find the selected artifact
        const artifact = flatArtifacts.find((a: PrimeArtifact) => a.id === artifactId);
        if (!artifact) {
          console.error('Artifact not found with ID:', artifactId);
          throw new Error('Artifact not found');
        }
        console.log('Selected artifact:', artifact);
        setSelectedArtifact(artifact);

        // Fetch relationships using the proper API endpoint
        const relationshipsResponse = await RelationshipService.getAll();
        console.log('Relationships response:', relationshipsResponse);
        console.log('Relationships type:', typeof relationshipsResponse);
        console.log('Is Array?', Array.isArray(relationshipsResponse));

        if (!Array.isArray(relationshipsResponse)) {
          console.error('Relationships response is not an array:', relationshipsResponse);
          throw new Error('Invalid relationships data received from server');
        }

        setRelationships(relationshipsResponse);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [artifactId, navigate, flatArtifacts, fetchArtifacts]);

  // Add a separate useEffect to fetch artifacts on component mount
  useEffect(() => {
    const loadArtifacts = async () => {
      try {
        console.log('Loading artifacts on component mount');
        await fetchArtifacts();
        console.log('Artifacts loaded:', flatArtifacts.length);
      } catch (err) {
        console.error('Failed to fetch artifacts:', err);
      }
    };

    void loadArtifacts();
  }, [fetchArtifacts, flatArtifacts.length]);

  const renderVisualization = () => {
    if (!selectedArtifact) return null;

    const props = {
      selectedArtifact,
      relationships,
      onNodeClick: (artifact: PrimeArtifact) => {
        window.location.href = `/artifacts/${artifact.id}/relationships`;
      },
    };

    // Get all related artifacts from relationships
    const relatedArtifactIds = new Set<string>();
    relationships.forEach((rel) => {
      if (rel.source_artifact_id) relatedArtifactIds.add(rel.source_artifact_id);
      if (rel.target_artifact_id) relatedArtifactIds.add(rel.target_artifact_id);
    });

    // Find all related artifacts from flatArtifacts
    const relatedArtifacts = flatArtifacts.filter((a) => relatedArtifactIds.has(a.id));

    // Convert PrimeArtifact to Artifact for NetworkView
    const networkArtifacts: Artifact[] = relatedArtifacts.map((artifact) => ({
      id: artifact.id,
      name: artifact.name,
      description: artifact.description || undefined,
      type: artifact.artifact_type_id,
      state: 'active',
      created_at: artifact.created_at || new Date().toISOString(),
      updated_at: artifact.updated_at || artifact.created_at || new Date().toISOString(),
      artifact_type_id: artifact.artifact_type_id,
    }));

    switch (visualizationType) {
      case 'radial':
        return <RadialView {...props} />;
      case 'layered':
        return <LayeredView {...props} />;
      case 'hierarchical':
        return (
          <HierarchicalView
            artifacts={[selectedArtifact]}
            relationships={relationships}
            onNodeClick={(artifact) => {
              window.location.href = `/artifacts/${artifact.id}/relationships`;
            }}
          />
        );
      case 'network':
        return (
          <NetworkView
            artifacts={networkArtifacts}
            relationships={relationships}
            selectedArtifactId={selectedArtifact.id}
            onArtifactSelect={(artifactId) => {
              window.location.href = `/artifacts/${artifactId}/relationships`;
            }}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Feature Not Available</h2>
        <p>The Relationship Visualization feature is not yet available.</p>
        <p>Error: {error}</p>
        <p>
          The database table for artifact relationships needs to be created before this feature can
          be used.
        </p>
        <button className="back-button" onClick={() => navigate('/prime/artifacts')}>
          Back to Artifacts
        </button>
      </div>
    );
  }

  if (!selectedArtifact) {
    return <div className="error">No artifact selected</div>;
  }

  // Add check for empty relationships
  if (!relationships || relationships.length === 0) {
    return (
      <div className="relationship-visualization-page">
        <div className="visualization-header">
          <h1>{selectedArtifact.name} - Relationships</h1>
        </div>
        <div className="empty-relationships-message">
          <h2>No Relationships Found</h2>
          <p>There are no relationships defined for this artifact yet.</p>
          <p>To create relationships, you need to:</p>
          <ol>
            <li>Navigate to the artifact details page</li>
            <li>Use the "Add Relationship" button to create connections with other artifacts</li>
            <li>Define the relationship type and direction</li>
          </ol>
          <button className="back-button" onClick={() => navigate('/prime/artifacts')}>
            Back to Artifacts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relationship-visualization-page">
      <div className="visualization-header">
        <h1>{selectedArtifact.name} - Relationships</h1>
        <div className="visualization-controls">
          <select
            value={visualizationType}
            onChange={(e) => setVisualizationType(e.target.value as VisualizationType)}
          >
            <option value="radial">Radial View</option>
            <option value="layered">Layered View</option>
            <option value="hierarchical">Hierarchical View</option>
            <option value="network">Network View</option>
          </select>
        </div>
      </div>
      <div className="visualization-container">{renderVisualization()}</div>
    </div>
  );
};

export default RelationshipVisualizationPage;
